import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import HistoryIcon from "@mui/icons-material/History";
import LeaderboardIcon from "@mui/icons-material/Leaderboard";
import axios from "axios";
import {
  clearRecentProducts,
  enrichRecentProducts,
  readRecentProducts,
  saveRecentProduct,
} from "../utils/recentProducts";
import "./Products.css";

const Games = ({
  title = "Games",
  forceTitle = false,
  showRecent = true,
  showQuickActions = true,
}) => {
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [recentProducts, setRecentProducts] = useState([]);
  const [loading, setLoading] = useState(false);

  const mobileQuickActions = [
    {
      label: "Add Coin",
      path: "/add-balance",
      icon: <AddCircleIcon />,
      tone: "coin",
    },
    {
      label: "Orders",
      path: "/orders",
      icon: <ReceiptLongIcon />,
      tone: "orders",
    },
    {
      label: "History",
      path: "/add-balance",
      icon: <HistoryIcon />,
      tone: "history",
    },
    {
      label: "Ranks",
      path: "/leaderboard",
      icon: <LeaderboardIcon />,
      tone: "ranks",
      badge: "NEW",
    },
  ];

  const imgUrl = (p) => {
    if (!p) return "";
    let v = String(p).replace(/\\/g, "/").trim();

    if (v.includes(":/")) {
      const filename = v.split("/").pop();
      return `/productImages/${filename}`;
    }

    if (/^https?:\/\//i.test(v)) return v;
    if (v.startsWith("/")) return v;
    return `/${v}`;
  };

  const isOutOfStock = (item) => {
    if (item?.stock === "no") return true;
    if (item?.in_stock === false) return true;
    if (typeof item?.stock_qty === "number" && item.stock_qty <= 0) return true;
    return false;
  };

  const sectionTitle =
    forceTitle
      ? title
      : products.find((item) => String(item?.category || "").trim())?.category ||
        title;

  const productPath = (item) => `/product/${encodeURIComponent(item?.name || "")}`;

  const openProduct = (item, out) => {
    if (out) return;
    setRecentProducts(saveRecentProduct(item));
    navigate(productPath(item));
  };

  const getAllProducts = async () => {
    try {
      setLoading(true);
      const res = await axios.get("/api/product/all-products");
      if (res.data?.success) {
        const list = Array.isArray(res.data.data) ? res.data.data : [];
        setProducts(list);
        setRecentProducts(enrichRecentProducts(readRecentProducts(), list));
      } else {
        setProducts([]);
        setRecentProducts(readRecentProducts());
      }
    } catch (error) {
      console.log(error);
      setProducts([]);
      setRecentProducts(readRecentProducts());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setRecentProducts(readRecentProducts());
    getAllProducts();
  }, []);

  if (loading) return <div className="loader-container">Loading...</div>;

  return (
    <div className="explore-products-container">
      {showRecent && recentProducts.length > 0 && (
        <section className="recentViewedSection" aria-label="Recently viewed products">
          <div className="recentViewedHeader">
            <h2 className="recentViewedTitle">Recently Viewed</h2>
            <button
              type="button"
              className="recentClearBtn"
              title="Clear recently viewed"
              aria-label="Clear recently viewed"
              onClick={() => setRecentProducts(clearRecentProducts())}
            >
              <DeleteOutlineIcon fontSize="small" />
            </button>
          </div>

          <div className="recentViewedList">
            {recentProducts.map((item) => {
              const out = isOutOfStock(item);
              const label =
                String(item?.tag || "").trim() ||
                String(item?.category || "").trim() ||
                "Top Up";

              return (
                <button
                  type="button"
                  key={item?._id || item?.name}
                  className={`recentProductCard ${out ? "isDisabled" : ""}`}
                  onClick={() => openProduct(item, out)}
                  disabled={out}
                >
                  <img src={imgUrl(item?.image)} alt={item?.name || "product"} />
                  <span className="recentProductCopy">
                    <small>{out ? "Out Of Stock" : label}</small>
                    <strong>{item?.name || "-"}</strong>
                  </span>
                </button>
              );
            })}
          </div>
        </section>
      )}

      {showQuickActions && (
        <section className="mobileQuickActions" aria-label="Quick actions">
          {mobileQuickActions.map((action) => (
            <button
              type="button"
              key={action.label}
              className="mobileQuickAction"
              onClick={() => navigate(action.path)}
            >
              {action.badge && <span className="quickActionBadge">{action.badge}</span>}
              <span className={`quickActionIcon ${action.tone}`}>
                {action.icon}
              </span>
              <span className="quickActionText">{action.label}</span>
            </button>
          ))}
        </section>
      )}

      <section className="productSection" id="games">
        <div className="productSectionHeader">
          <h2 className="sectionTitle">
            {sectionTitle}
            <LocalFireDepartmentIcon className="sectionFireIcon" />
          </h2>
        </div>

        <div className="productGrid">
          {products.map((item) => {
            const out = isOutOfStock(item);
            const tag = String(item?.tag || "").trim();
            const category = String(item?.category || "").trim();
            const badge = out ? "Out Of Stock" : tag || category;

            return (
              <div
                key={item?._id || item?.name}
                className={`productCard ${out ? "outStock" : ""}`}
                onClick={() => openProduct(item, out)}
              >
                {badge && (
                  <span className={`productTagBadge ${out ? "isOut" : ""}`}>
                    {badge}
                  </span>
                )}

                <div className="imgWrap">
                  <div className="productImageRing">
                    <img src={imgUrl(item?.image)} alt={item?.name || "product"} />
                  </div>
                </div>

                <div className="cardBody">
                  {category && <span className="productCategory">{category}</span>}
                  <h6 className="productName">{item?.name}</h6>
                  <button
                    type="button"
                    className="productAction"
                    disabled={out}
                    onClick={(e) => {
                      e.stopPropagation();
                      openProduct(item, out);
                    }}
                  >
                    {out ? "Unavailable" : "Top Up"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
};

export default Games;
